import { gaussianKDE } from "@/lib/science/plot-density";
import { SCI_PALETTE } from "@/lib/science/sci-chart";
import { cartesianAxis, PlotShell } from "@/lib/science/sci-plot";
import { m } from "@/paraglide/messages";

const EXAMPLE =
  "Jan,Feb,Mar,Apr\n12,15,19,24\n13,16,20,25\n11,14,18,23\n14,17,21,26\n12,15,19,24\n13,18,22,27\n15,16,20,25\n11,14,19,23\n13,17,21,26\n12,16,20,24";

export function RidgelinePlot() {
  return (
    <PlotShell
      toolId="ridgeline-plot"
      inputLabel={m.plot_input_label()}
      placeholder={m.ridge_placeholder()}
      example={EXAMPLE}
      exampleLabel={m.plot_load_example()}
      hint={m.plot_hint_empty()}
      height={440}
      buildOption={(data, theme) => {
        const groups = data.colHeaders.map((name, ci) => {
          const values = data.matrix
            .map((row) => row[ci])
            .filter((n) => Number.isFinite(n));
          return { name, kde: gaussianKDE(values, 96) };
        });
        const n = groups.length;
        const maxDens = Math.max(1e-9, ...groups.flatMap((g) => g.kde.ys));
        const scale = 1.5 / maxDens; // one ridge may overlap ~1.5 rows for depth

        return {
          tooltip: { trigger: "axis" },
          grid: { left: 70, right: 20, top: 20, bottom: 36 },
          xAxis: { type: "value", ...cartesianAxis(theme) },
          yAxis: {
            type: "value",
            min: -0.5,
            max: n,
            interval: 1,
            axisLabel: {
              color: theme.muted,
              fontSize: 11,
              // biome-ignore lint/suspicious/noExplicitAny: ECharts formatter value.
              // Row gi sits at baseline = n-1-gi, so the label at y=v is group n-1-v.
              formatter: (v: any) => groups[n - 1 - Math.round(v)]?.name ?? "",
            },
            axisLine: { lineStyle: { color: theme.border } },
            splitLine: { show: false },
          },
          series: groups.map((g, gi) => {
            const baseline = n - 1 - gi; // first group at the top
            const color = SCI_PALETTE[gi % SCI_PALETTE.length];
            return {
              type: "line",
              smooth: true,
              showSymbol: false,
              z: gi,
              data: g.kde.xs.map((x, i) => [x, baseline + g.kde.ys[i] * scale]),
              lineStyle: { color, width: 1.5 },
              areaStyle: { color, opacity: 0.4, origin: baseline },
            };
          }),
        };
      }}
    />
  );
}
